// 실적 집계 순수 함수 — ConsolidatedView(제품/국가 마진 집계)와 총판 데스크
// 홈 보드가 동일한 수식을 공유하도록 추출 (distributor-desk Design §3, FR-6).
// GT 규칙: 확정 실적 행(contract_sales)만 집계 — 완료 딜 자동집계 금지.
import type { ContractRow, ContractSale, Product } from "@/types";
import { saleCalc } from "@/lib/margin/model";
import { saleToModel } from "./convert";

export type Agg = { qty: number; revUsd: number; revKrw: number; cost: number; margin: number };

export const zeroAgg = (): Agg => ({ qty: 0, revUsd: 0, revKrw: 0, cost: 0, margin: 0 });

function addSale(a: Agg, r: ReturnType<typeof saleCalc>, qty: number): void {
  a.qty += qty || 0;
  a.revUsd += r.revenueUsd;
  a.revKrw += r.revenueKrw;
  a.cost += r.costTotal;
  a.margin += r.marginKrw;
}

export interface SalesAggregation {
  byProduct: Map<string, Agg>;
  byCountry: Map<string, Agg>;
  total: Agg;
  count: number;
}

/**
 * 확정 실적을 제품별/국가별/전체로 집계. `year`="all" 이면 전체 연도.
 * 국가 키 = 계약의 country_name ?? destination_country, 제품 키 = products
 * 매칭 model_name ?? sim_product (ConsolidatedView 원본 로직과 동일).
 */
export function aggregateSales(
  sales: ContractSale[],
  contracts: ContractRow[],
  products: Product[],
  year: string,
): SalesAggregation {
  const byId = new Map(contracts.map((c) => [c.id, c]));
  const productName = (ct: ContractRow) =>
    products.find((p) => p.id === ct.product_id)?.model_name ?? ct.sim_product ?? "미지정";
  const countryName = (ct: ContractRow) => ct.country_name ?? ct.destination_country ?? "미지정";

  const byProduct = new Map<string, Agg>();
  const byCountry = new Map<string, Agg>();
  const total = zeroAgg();
  let count = 0;

  for (const s of sales) {
    if (year !== "all" && !s.sale_date.startsWith(year)) continue;
    const ct = byId.get(s.contract_id);
    const c = saleCalc(saleToModel(s));
    const pk = ct ? productName(ct) : "미지정";
    const ck = ct ? countryName(ct) : "미지정";
    if (!byProduct.has(pk)) byProduct.set(pk, zeroAgg());
    if (!byCountry.has(ck)) byCountry.set(ck, zeroAgg());
    addSale(byProduct.get(pk)!, c, s.qty);
    addSale(byCountry.get(ck)!, c, s.qty);
    addSale(total, c, s.qty);
    count++;
  }
  return { byProduct, byCountry, total, count };
}

/** 셀 키 = product␟country (단위구분자 U+241F 로 충돌 회피). */
const mkey = (p: string, c: string) => `${p}␟${c}`;

export interface MatrixAggregation {
  products: string[]; // 행 키 (정렬)
  countries: string[]; // 열 키 (정렬)
  cell: Map<string, Agg>; // key = mkey(product, country)
  rowTotal: Map<string, Agg>; // 제품별 합 (= aggregateSales.byProduct 와 일치)
  colTotal: Map<string, Agg>; // 국가별 합 (= aggregateSales.byCountry 와 일치)
  total: Agg;
  count: number;
}

/**
 * 확정 실적을 제품×국가 2차원으로 교차 집계 (경영 렌즈 매트릭스, Design §6).
 * 키 규칙·수식은 aggregateSales 와 동일 — 열합=byCountry, 행합=byProduct 와 수치 일치.
 * 계산은 saleCalc 한 곳에서만 (회귀 안전망 단일화).
 */
export function aggregateMatrix(
  sales: ContractSale[],
  contracts: ContractRow[],
  products: Product[],
  year: string,
): MatrixAggregation {
  const byId = new Map(contracts.map((c) => [c.id, c]));
  const productName = (ct: ContractRow) =>
    products.find((p) => p.id === ct.product_id)?.model_name ?? ct.sim_product ?? "미지정";
  const countryName = (ct: ContractRow) => ct.country_name ?? ct.destination_country ?? "미지정";

  const cell = new Map<string, Agg>();
  const rowTotal = new Map<string, Agg>();
  const colTotal = new Map<string, Agg>();
  const total = zeroAgg();
  const pset = new Set<string>();
  const cset = new Set<string>();
  let count = 0;

  for (const s of sales) {
    if (year !== "all" && !s.sale_date.startsWith(year)) continue;
    const ct = byId.get(s.contract_id);
    const r = saleCalc(saleToModel(s));
    const pk = ct ? productName(ct) : "미지정";
    const ck = ct ? countryName(ct) : "미지정";
    pset.add(pk);
    cset.add(ck);
    const k = mkey(pk, ck);
    if (!cell.has(k)) cell.set(k, zeroAgg());
    if (!rowTotal.has(pk)) rowTotal.set(pk, zeroAgg());
    if (!colTotal.has(ck)) colTotal.set(ck, zeroAgg());
    addSale(cell.get(k)!, r, s.qty);
    addSale(rowTotal.get(pk)!, r, s.qty);
    addSale(colTotal.get(ck)!, r, s.qty);
    addSale(total, r, s.qty);
    count++;
  }

  return {
    products: [...pset].sort(),
    countries: [...cset].sort(),
    cell,
    rowTotal,
    colTotal,
    total,
    count,
  };
}

/** 매트릭스 셀 조회 (없으면 0). */
export function matrixCell(m: MatrixAggregation, product: string, country: string): Agg {
  return m.cell.get(mkey(product, country)) ?? zeroAgg();
}
